import React, { forwardRef, useImperativeHandle, useState, useEffect, useCallback } from 'react';
import CustomTextField from '../MuiComponent/TextField/CustomTextField';
import { Button, Alert, Snackbar, FormControl, InputLabel, Select, MenuItem, CircularProgress, Autocomplete, TextField, Box } from "@mui/material";
import CreatePostFetch from '../../ServerCalls/CreatePostFetch';
import CreateGetFetch from '../../ServerCalls/CreateGetFetch';
import { useForm, Controller } from "react-hook-form"
import CacheManager from '../../Utils/CacheManage';

const AvifaunaSitesUpdate = forwardRef((props, _passedRef) => {

    const [[snackBarVal, snackBarMsg, snackBarType], setSnackBar] = useState([false, "", "info"]);
    const [avifaunaSites, setAvifaunaSites] = useState([]);
    const [loadingSites, setLoadingSites] = useState(false);

    const postBuilder = CreatePostFetch();
    const getBuilder = CreateGetFetch();
    const cacheManager = new CacheManager();

    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
        getValues
    } = useForm({
        defaultValues: {
            code: '',
            locationName: '',
            avifaunaSiteName: "",
            avifaunaLatitude: "",
            avifaunaLongitude: "",
            avifaunaState: ""
        }
    });

    // Fetch Avifauna Sites from API on component mount
    useEffect(() => {
        fetchAvifaunaSites();
    }, []);

    const fetchAvifaunaSites = async () => {
        setLoadingSites(true);
        try {
            let response = await getBuilder.caller("/getAvifaunaSites");
            let sitesData = await response.json();

            if (sitesData && Array.isArray(sitesData.sites)) {
                setAvifaunaSites(sitesData.sites);
            } else if (Array.isArray(sitesData)) {
                setAvifaunaSites(sitesData);
            } else {
                setAvifaunaSites([]);
            }
        } catch (error) {
            console.error("Error fetching Avifauna Sites:", error);
            setSnackBar([true, "Failed to load Avifauna Sites", "error"]);
            setAvifaunaSites([]);
        } finally {
            setLoadingSites(false);
        }
    };

    const handleSiteChange = (selectedSite) => {
        if (selectedSite) {
            setValue("avifaunaLatitude", selectedSite.latitude ?? "");
            setValue("avifaunaLongitude", selectedSite.longitude ?? "");
            setValue("avifaunaState", selectedSite.state_name ?? "");
        }
    };

    useImperativeHandle(_passedRef, () => ({
        setFormValues: handleValueChange
    }))

    const handleValueChange = (code, locationName, avifaunaSiteName = null, latitude = null, longitude = null, state = null) => {
        setValue("code", code, { shouldValidate: false });
        setValue("locationName", locationName, { shouldValidate: false });
        setValue("avifaunaSiteName", avifaunaSiteName || "", { shouldValidate: false });
        setValue("avifaunaLatitude", latitude || "", { shouldValidate: false });
        setValue("avifaunaLongitude", longitude || "", { shouldValidate: false });
        setValue("avifaunaState", state || "", { shouldValidate: false });
    }

    const handleUpsertCall = async (data) => {
        cacheManager.removeEntry("localityMap");

        const selectedSite = avifaunaSites.find(
            (site) => site.location_name === data.avifaunaSiteName
        );

        if (!selectedSite) {
            setSnackBar([true, "Please select a valid Avifauna Site", "error"]);
            return;
        }

        const formBody = new FormData();
        const payload = {
            code: data.code,
            avifauna_site_id: selectedSite.id
        };
        formBody.append("data", JSON.stringify(payload));

        let response;
        try {
            response = await postBuilder.caller("/updateAvifaunaSites", formBody);
        } catch (e) {
            setSnackBar([true, "Operation Failed", "error"]);
            return;
        }

        if (response.status === 200) {
            if (props.onUpdateSuccess) {
                await props.onUpdateSuccess();
            }
            setSnackBar([true, "Avifauna site updated successfully", "success"]);
            // Clear form after successful submission
            setTimeout(() => {
                setValue("code", "");
                setValue("locationName", "");
                setValue("avifaunaSiteName", "");
                setValue("avifaunaLatitude", "");
                setValue("avifaunaLongitude", "");
                setValue("avifaunaState", "");
            }, 500);
        } else {
            setSnackBar([true, "Operation Failed", "error"]);
        }
    }

    return (
        <div className='user_crud-frame'>
            <div className='user_crud-card'>
                <div className='user_operation_container'>
                    <div className='user-operation_label'>
                        Map Avifauna Sites to Locality
                    </div>

                    <Controller
                        name='code'
                        control={control}
                        rules={{
                            pattern: {
                                value: /^[0-9]*$/,
                                message: "Only Numeric Values Allowed"
                            }
                        }}
                        render={({ field }) => (
                            <CustomTextField
                                fullWidth
                                type='text'
                                label="Code"
                                {...field}
                                InputLabelProps={{ shrink: field.value ? true : false }}
                                error={errors.code ? true : false}
                                helperText={errors.code ? errors.code.message : ""}
                                inputProps={{ readOnly: true }}
                            />
                        )}
                    />

                    <Controller
                        name='locationName'
                        control={control}
                        rules={{
                            pattern: {
                                value: /^[a-zA-Z0-9\s\-_.,]*$/,
                                message: "Invalid Location Name Format"
                            }
                        }}
                        render={({ field }) => (
                            <CustomTextField
                                fullWidth
                                label="Location Name"
                                type='text'
                                {...field}
                                InputLabelProps={{ shrink: field.value ? true : false }}
                                error={errors.locationName ? true : false}
                                helperText={errors.locationName ? errors.locationName.message : ""}
                                inputProps={{ readOnly: true }}
                            />
                        )}
                    />

                    <Controller
                        name='avifaunaSiteName'
                        control={control}
                        render={({ field: { onChange, value, ...field } }) => {
                            const selectedSite = avifaunaSites.find(
                                (site) => site.location_name === value
                            ) || null;

                            return (
                                <Autocomplete
                                    {...field}
                                    options={avifaunaSites}
                                    value={selectedSite}
                                    getOptionLabel={(option) => {
                                        if (typeof option === 'string') return option;
                                        return option.location_name || '';
                                    }}
                                    isOptionEqualToValue={(option, val) => {
                                        if (!option || !val) return false;
                                        return option.id === val.id;
                                    }}
                                    loading={loadingSites}
                                    onChange={(_, newValue) => {
                                        onChange(newValue ? newValue.location_name : '');
                                        if (newValue) {
                                            handleSiteChange(newValue);
                                        } else {
                                            setValue("avifaunaLatitude", "");
                                            setValue("avifaunaLongitude", "");
                                            setValue("avifaunaState", "");
                                        }
                                    }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Avifauna Site Name"
                                            error={errors.avifaunaSiteName ? true : false}
                                            helperText={errors.avifaunaSiteName ? errors.avifaunaSiteName.message : "Search and select a site"}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loadingSites ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                    noOptionsText="No sites available"
                                    filterOptions={(options, { inputValue }) => {
                                        if (!inputValue) return options;
                                        return options.filter(option =>
                                            option.location_name?.toLowerCase().includes(inputValue.toLowerCase())
                                        );
                                    }}
                                    fullWidth
                                />
                            );
                        }}
                    />

                    <div style={{ display: "flex", flexDirection: "row", gap: "15px" }}>
                        <Controller
                            name='avifaunaLatitude'
                            control={control}
                            rules={{
                                pattern: {
                                    value: /^-?\d*\.?\d*$/,
                                    message: "Invalid Latitude Format"
                                }
                            }}
                            render={({ field }) => (
                                <CustomTextField
                                    fullWidth
                                    label="Avifauna Latitude"
                                    type='text'
                                    {...field}
                                    InputLabelProps={{ shrink: field.value ? true : false }}
                                    error={errors.avifaunaLatitude ? true : false}
                                    helperText={errors.avifaunaLatitude ? errors.avifaunaLatitude.message : ""}
                                    inputProps={{ readOnly: true }}
                                />
                            )}
                        />

                        <Controller
                            name='avifaunaLongitude'
                            control={control}
                            rules={{
                                pattern: {
                                    value: /^-?\d*\.?\d*$/,
                                    message: "Invalid Longitude Format"
                                }
                            }}
                            render={({ field }) => (
                                <CustomTextField
                                    fullWidth
                                    label="Avifauna Longitude"
                                    type='text'
                                    {...field}
                                    InputLabelProps={{ shrink: field.value ? true : false }}
                                    error={errors.avifaunaLongitude ? true : false}
                                    helperText={errors.avifaunaLongitude ? errors.avifaunaLongitude.message : ""}
                                    inputProps={{ readOnly: true }}
                                />
                            )}
                        />
                    </div>

                    <Controller
                        name='avifaunaState'
                        control={control}
                        rules={{
                            pattern: {
                                value: /^[a-zA-Z\s]*$/,
                                message: "Only Alphabetic Characters Allowed"
                            }
                        }}
                        render={({ field }) => (
                            <CustomTextField
                                fullWidth
                                label="Avifauna State"
                                type='text'
                                {...field}
                                InputLabelProps={{ shrink: field.value ? true : false }}
                                error={errors.avifaunaState ? true : false}
                                helperText={errors.avifaunaState ? errors.avifaunaState.message : ""}
                                inputProps={{ readOnly: true }}
                            />
                        )}
                    />

                    <Button
                        variant="contained"
                        size='small'
                        color='success'
                        onClick={handleSubmit(handleUpsertCall)}
                    >
                        Update
                    </Button>
                </div>
            </div>

            <Snackbar
                open={snackBarVal}
                key={"SnackBar"}
                autoHideDuration={1000}
                onClose={() => { setSnackBar(prevState => [false, prevState[1], prevState[2]]) }}
            >
                <Alert
                    onClose={() => setSnackBar([false, snackBarMsg, snackBarType])}
                    severity={snackBarType}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackBarMsg}
                </Alert>
            </Snackbar>
        </div>
    )
})

export default AvifaunaSitesUpdate;